// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: effeda34-5763-42ea-b65f-f07b1dc1759e

// ---------------------------------------------------------------------------
// Bulk tools — the two things every real client inventory (400–4,000 apps)
// needs on day one:
//
//   1. Bulk edit      — filter a slice of the portfolio, then stamp one or more
//                       field values across every matching application.
//   2. Find duplicates — fuzzy-match application names, review candidates side
//                       by side, and merge the poorer record into the richer
//                       one (survivor wins; blanks are back-filled).
//
// Everything here goes through the same store API the rest of the app uses
// (listFactSheets / saveFactSheet / deleteFactSheet), so it works identically
// against localStorage today and DynamoDB via the API tomorrow.
//
// The merge path DELETES data, so it is deliberately explicit: the survivor is
// saved FIRST and the merged-away records are only removed once that save has
// succeeded, and the confirm step spells out every field that will be copied.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stack, Tabs, Tab, TextField, MenuItem, InputAdornment, Table, TableHead,
  TableRow, TableCell, TableBody, Checkbox, Switch, FormControlLabel,
  LinearProgress, Alert, Chip, Slider, Divider, IconButton, Paper, Radio,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { allFieldsFor, resolvePicklist, FIELD_TYPES, PICKLISTS } from './schema';
import { CompletionBar } from './ui';
import store from './store';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

// How many rows we actually paint in the bulk-edit table. Selection always
// applies to the full matching set — the cap is purely a rendering guard so a
// 4,000-row tenant does not lock the browser.
const ROW_CAP = 300;
const GROUP_CAP = 100;

const isBlank = (v) => v === undefined || v === null || v === '';

// ---------------------------------------------------------------------------
// Fuzzy name matching (self-contained — no libraries)
// ---------------------------------------------------------------------------
// Normalise: lowercase, "&" -> "and", strip punctuation/whitespace, drop legal
// and boilerplate suffix tokens. Then score with the better of a Levenshtein
// ratio over the squashed key and a token-overlap score, so both
// "Kronos Workforce" vs "Kronos Workfoce" (typo) and
// "Epic" vs "Epic Hyperspace" (containment) are caught.
const NOISE_TOKENS = new Set([
  'inc', 'llc', 'ltd', 'llp', 'lp', 'corp', 'corporation', 'co', 'company',
  'plc', 'gmbh', 'limited', 'the', 'intl', 'international', 'holdings',
]);

function normalizeName(name) {
  const flat = String(name || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const all = flat.split(' ').filter(Boolean);
  let tokens = all.filter((t) => !NOISE_TOKENS.has(t));
  if (!tokens.length) tokens = all; // name was entirely noise — keep it as-is
  return { key: tokens.join(''), tokens };
}

function levenshtein(a, b) {
  const n = a.length;
  const m = b.length;
  if (!n) return m;
  if (!m) return n;
  let prev = new Array(m + 1);
  let cur = new Array(m + 1);
  for (let j = 0; j <= m; j++) prev[j] = j;
  for (let i = 1; i <= n; i++) {
    cur[0] = i;
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= m; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      const del = prev[j] + 1;
      const ins = cur[j - 1] + 1;
      const sub = prev[j - 1] + cost;
      cur[j] = del < ins ? (del < sub ? del : sub) : ins < sub ? ins : sub;
    }
    const swap = prev;
    prev = cur;
    cur = swap;
  }
  return prev[m];
}

export function nameSimilarity(a, b) {
  const A = a.key;
  const B = b.key;
  if (!A || !B) return 0;
  if (A === B) return 1;

  // Levenshtein ratio over the squashed keys (skipped when the lengths are
  // wildly different or the strings are long enough to be expensive).
  const maxLen = Math.max(A.length, B.length);
  let lev = 0;
  if (Math.abs(A.length - B.length) / maxLen <= 0.5 && maxLen <= 80) {
    lev = 1 - levenshtein(A, B) / maxLen;
  }

  // Token overlap: Jaccard, plus a slightly discounted containment score so a
  // short name fully contained in a longer one still surfaces.
  const sa = new Set(a.tokens);
  const sb = new Set(b.tokens);
  let inter = 0;
  sa.forEach((t) => { if (sb.has(t)) inter += 1; });
  const union = sa.size + sb.size - inter;
  const jaccard = union ? inter / union : 0;
  const smaller = Math.min(sa.size, sb.size);
  const containment = smaller ? (inter / smaller) * 0.9 : 0;

  return Math.max(lev, jaccard, containment);
}

// Blocked pairwise comparison + union-find grouping. Blocking (shared 4-char
// key prefix or shared token prefix) keeps this near-linear on 4,000 rows
// instead of the 8M comparisons a naive O(n^2) sweep would need.
export function findDuplicateGroups(records, threshold) {
  const n = records.length;
  if (n < 2) return { groups: [], truncated: false };

  const norms = new Array(n);
  for (let i = 0; i < n; i++) norms[i] = normalizeName(records[i].name);

  const buckets = new Map();
  const push = (k, i) => {
    if (!k) return;
    const arr = buckets.get(k);
    if (arr) arr.push(i);
    else buckets.set(k, [i]);
  };
  for (let i = 0; i < n; i++) {
    const t = records[i].tenantId || '';
    push(`${t}|p|${norms[i].key.slice(0, 4)}`, i);
    norms[i].tokens.forEach((tok) => push(`${t}|t|${tok.slice(0, 6)}`, i));
  }

  const parent = new Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (x) => {
    let r = x;
    while (parent[r] !== r) { parent[r] = parent[parent[r]]; r = parent[r]; }
    return r;
  };
  const join = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  const MAX_COMPARISONS = 600000;
  const MAX_BUCKET = 400; // a pathologically common token — the prefix bucket still covers it
  const seenPairs = new Set();
  const edges = [];
  let comparisons = 0;
  let truncated = false;

  for (const arr of buckets.values()) {
    if (truncated) break;
    if (arr.length < 2 || arr.length > MAX_BUCKET) continue;
    for (let x = 0; x < arr.length && !truncated; x++) {
      for (let y = x + 1; y < arr.length; y++) {
        const i = arr[x];
        const j = arr[y];
        if (records[i].tenantId !== records[j].tenantId) continue;
        const pk = i < j ? `${i}:${j}` : `${j}:${i}`;
        if (seenPairs.has(pk)) continue;
        seenPairs.add(pk);
        comparisons += 1;
        if (comparisons > MAX_COMPARISONS) { truncated = true; break; }
        const score = nameSimilarity(norms[i], norms[j]);
        if (score >= threshold) {
          edges.push({ i, j, score });
          join(i, j);
        }
      }
    }
  }

  const byRoot = new Map();
  edges.forEach((e) => {
    const root = find(e.i);
    const g = byRoot.get(root);
    if (g) { g.score = Math.max(g.score, e.score); g.set.add(e.i); g.set.add(e.j); }
    else byRoot.set(root, { score: e.score, set: new Set([e.i, e.j]) });
  });

  const groups = [];
  byRoot.forEach((g) => {
    const members = [...g.set]
      .map((i) => records[i])
      .sort((a, b) => (b.completion || 0) - (a.completion || 0) || a.name.localeCompare(b.name));
    if (members.length < 2) return;
    groups.push({
      id: members.map((m) => m.id).slice().sort().join('~'),
      score: g.score,
      members,
    });
  });
  groups.sort((a, b) => b.score - a.score || a.members[0].name.localeCompare(b.members[0].name));
  return { groups, truncated };
}

// ---------------------------------------------------------------------------
// Merge plan — pure, so the confirm dialog shows exactly what will be written.
// ---------------------------------------------------------------------------
const tagKeyOf = (t) => `${t && t.group}||${t && t.value}`;
const integrationKeyOf = (it) =>
  [it && it.to, it && it.via, it && it.protocol, it && it.port, it && it.direction, it && it.dataType].join('||');

export function planMerge(survivor, losers) {
  const fields = { ...(survivor.fields || {}) };
  const filled = [];
  losers.forEach((l) => {
    Object.entries(l.fields || {}).forEach(([k, v]) => {
      if (k === 'name') return;           // never rename the survivor
      if (isBlank(v) || v === false) return;
      if (!isBlank(fields[k]) && fields[k] !== false) return; // survivor wins
      fields[k] = v;
      filled.push({ key: k, value: v, from: l.name });
    });
  });

  const tags = [...(survivor.tags || [])];
  const tagSeen = new Set(tags.map(tagKeyOf));
  const tagsAdded = [];
  losers.forEach((l) => {
    (l.tags || []).forEach((t) => {
      const k = tagKeyOf(t);
      if (tagSeen.has(k)) return;
      tagSeen.add(k);
      tags.push(t);
      tagsAdded.push(t);
    });
  });

  const integrations = [...(survivor.integrations || [])];
  const intSeen = new Set(integrations.map(integrationKeyOf));
  const integrationsAdded = [];
  losers.forEach((l) => {
    (l.integrations || []).forEach((it) => {
      const k = integrationKeyOf(it);
      if (intSeen.has(k)) return;
      intSeen.add(k);
      integrations.push(it);
      integrationsAdded.push(it);
    });
  });

  // Relations are unioned by target id so links to other fact sheets are not
  // lost when the merged-away record is deleted.
  const relations = {};
  Object.entries(survivor.relations || {}).forEach(([k, arr]) => { relations[k] = [...(arr || [])]; });
  let relationsAdded = 0;
  losers.forEach((l) => {
    Object.entries(l.relations || {}).forEach(([k, arr]) => {
      relations[k] = relations[k] || [];
      const ids = new Set(relations[k].map((r) => r && r.id));
      (arr || []).forEach((r) => {
        if (!r || ids.has(r.id)) return;
        ids.add(r.id);
        relations[k].push(r);
        relationsAdded += 1;
      });
    });
  });

  const record = { ...survivor, fields, tags, integrations, relations };
  delete record.completion;
  delete record.resolvedRelations;
  return { record, filled, tagsAdded, integrationsAdded, relationsAdded };
}

// ---------------------------------------------------------------------------
// Value editor for one bulk-edit assignment
// ---------------------------------------------------------------------------
function ValueEditor({ field, value, onChange }) {
  if (!field) {
    return <TextField size="small" fullWidth disabled placeholder="Pick a field first" />;
  }
  if (field.type === FIELD_TYPES.BOOLEAN) {
    return (
      <FormControlLabel
        sx={{ ml: 0 }}
        control={<Switch size="small" checked={value === true} onChange={(e) => onChange(e.target.checked)} />}
        label={<span style={{ fontSize: 13 }}>{value === true ? 'Yes' : 'No'}</span>}
      />
    );
  }
  const picky =
    field.type === FIELD_TYPES.SELECT ||
    field.type === FIELD_TYPES.MULTISELECT ||
    field.type === FIELD_TYPES.RATING ||
    field.type === FIELD_TYPES.ARROWS;
  const options = picky ? resolvePicklist(field.picklist) : [];
  if (picky && options.length) {
    return (
      <TextField select size="small" fullWidth value={value === undefined ? '' : value}
        onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </TextField>
    );
  }
  if (field.type === FIELD_TYPES.NUMBER || field.type === FIELD_TYPES.CURRENCY || field.type === FIELD_TYPES.PERCENT) {
    return (
      <TextField size="small" fullWidth type="number" value={value === undefined || value === null ? '' : value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />
    );
  }
  if (field.type === FIELD_TYPES.DATE) {
    return (
      <TextField size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
        value={value === undefined ? '' : value} onChange={(e) => onChange(e.target.value)} />
    );
  }
  return (
    <TextField size="small" fullWidth multiline={field.type === FIELD_TYPES.TEXTAREA}
      maxRows={field.type === FIELD_TYPES.TEXTAREA ? 4 : 1}
      value={value === undefined ? '' : value} onChange={(e) => onChange(e.target.value)} />
  );
}

// Key fields shown side by side when comparing duplicate candidates.
const COMPARE_FIELDS = [
  'description', 'lifecycleState', 'applicationType', 'hostingType', 'locationType',
  'vendorName', 'applicationOwner', 'businessUnit', 'timeClassification', 'sixRClassification',
];

export default function BulkTools({ open, onClose, tenantId, onChanged }) {
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');

  // ---- Tab 1 state --------------------------------------------------------
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [lifeFilter, setLifeFilter] = useState('');
  const [dispFilter, setDispFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [edits, setEdits] = useState([{ key: '', value: '' }]);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  // ---- Tab 2 state --------------------------------------------------------
  const [sliderValue, setSliderValue] = useState(0.85);
  const [threshold, setThreshold] = useState(0.85);
  const [survivors, setSurvivors] = useState({});   // groupId -> factSheet id
  const [dropped, setDropped] = useState(() => new Set()); // `${groupId}::${id}` excluded from the merge
  const [confirm, setConfirm] = useState(null);
  const [merging, setMerging] = useState(false);
  const [mergeErr, setMergeErr] = useState('');
  const [mergeResult, setMergeResult] = useState(null);

  const reload = () => {
    setLoading(true);
    setLoadErr('');
    store.listFactSheets(tenantId, { type: 'Application' })
      .then((rows) => {
        setApps(rows || []);
        setSelected(new Set());
        setSurvivors({});
        setDropped(new Set());
      })
      .catch(() => setLoadErr('Could not load applications for this workspace.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setMergeResult(null);
    setMergeErr('');
    setConfirm(null);
    reload();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, tenantId]);

  // Editable Application fields, de-duplicated (hostingType/vendorName appear in
  // more than one section) and minus the ones bulk edit must not touch.
  const fields = useMemo(() => {
    const out = [];
    const seen = new Set();
    allFieldsFor('Application').forEach((f) => {
      if (f.key === 'name') return; // renaming everything at once is never the intent
      if (f.type === FIELD_TYPES.RELATION || f.type === FIELD_TYPES.LIFECYCLE || f.type === FIELD_TYPES.TAGS) return;
      if (seen.has(f.key)) return;
      seen.add(f.key);
      out.push(f);
    });
    return out;
  }, []);

  const fieldByKey = useMemo(() => {
    const m = {};
    fields.forEach((f) => { m[f.key] = f; });
    return m;
  }, [fields]);

  const labelFor = (key) => (fieldByKey[key] ? fieldByKey[key].label : key);

  const tagOptions = useMemo(() => {
    const counts = {};
    apps.forEach((fs) => (fs.tags || []).forEach((t) => {
      const k = `${t.group}:${t.value}`;
      counts[k] = (counts[k] || 0) + 1;
    }));
    return Object.entries(counts)
      .map(([k, count]) => ({ key: k, count }))
      .sort((a, b) => b.count - a.count);
  }, [apps]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps.filter((fs) => {
      if (q && !String(fs.name || '').toLowerCase().includes(q)) return false;
      if (lifeFilter && (fs.fields || {}).lifecycleState !== lifeFilter) return false;
      if (dispFilter) {
        const d = (fs.fields || {}).sixRClassification || (fs.fields || {}).cmDisposition || '';
        if (d !== dispFilter) return false;
      }
      if (tagFilter && !(fs.tags || []).some((t) => `${t.group}:${t.value}` === tagFilter)) return false;
      return true;
    });
  }, [apps, search, lifeFilter, dispFilter, tagFilter]);

  const visibleRows = filtered.slice(0, ROW_CAP);
  const selectedCount = selected.size;
  const allMatchingSelected = filtered.length > 0 && filtered.every((fs) => selected.has(fs.id));
  const someMatchingSelected = filtered.some((fs) => selected.has(fs.id));

  const toggleRow = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    setResult(null);
  };

  const toggleAllMatching = () => {
    const next = new Set(selected);
    if (allMatchingSelected) filtered.forEach((fs) => next.delete(fs.id));
    else filtered.forEach((fs) => next.add(fs.id));
    setSelected(next);
    setResult(null);
  };

  const setEditAt = (i, patch) => {
    const next = edits.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
    setEdits(next);
    setResult(null);
  };

  const validEdits = useMemo(
    () => edits.filter((e) => e.key && fieldByKey[e.key] && !isBlank(e.value)),
    [edits, fieldByKey]
  );

  const applyEdits = async () => {
    const ids = [...selected];
    if (!ids.length || !validEdits.length) return;
    const patch = {};
    validEdits.forEach((e) => { patch[e.key] = e.value; });

    const byId = new Map(apps.map((a) => [a.id, a]));
    setApplying(true);
    setProgress(0);
    setResult(null);

    let ok = 0;
    let failed = 0;
    const errors = [];
    for (let i = 0; i < ids.length; i++) {
      const fs = byId.get(ids[i]);
      if (!fs) { failed += 1; continue; }
      try {
        // Merge into the existing field bag — never replace it.
        await store.saveFactSheet({ ...fs, fields: { ...(fs.fields || {}), ...patch } });
        ok += 1;
      } catch (err) {
        failed += 1;
        if (errors.length < 5) errors.push(`${fs.name}: ${(err && err.message) || 'save failed'}`);
      }
      if (i % 10 === 0) {
        setProgress(Math.round(((i + 1) / ids.length) * 100));
        await new Promise((r) => setTimeout(r, 0)); // let the progress bar paint
      }
    }

    setProgress(100);
    setApplying(false);
    setResult({ ok, failed, errors, count: ids.length });
    if (onChanged) onChanged();
    reload();
  };

  // ---- Duplicates ---------------------------------------------------------
  const dupes = useMemo(() => {
    if (!open || tab !== 1) return { groups: [], truncated: false };
    return findDuplicateGroups(apps, threshold);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [apps, threshold, tab, open]);

  const survivorFor = (group) => {
    const chosen = survivors[group.id];
    if (chosen && group.members.some((m) => m.id === chosen)) return chosen;
    return group.members[0].id; // richest record (members are sorted by completion)
  };

  const losersFor = (group) => {
    const sid = survivorFor(group);
    return group.members.filter((m) => m.id !== sid && !dropped.has(`${group.id}::${m.id}`));
  };

  const toggleDropped = (groupId, id) => {
    const key = `${groupId}::${id}`;
    const next = new Set(dropped);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDropped(next);
  };

  const openConfirm = (group) => {
    const sid = survivorFor(group);
    const survivor = group.members.find((m) => m.id === sid);
    const losers = losersFor(group);
    if (!survivor || !losers.length) return;
    setMergeErr('');
    setConfirm({ group, survivor, losers, plan: planMerge(survivor, losers) });
  };

  const doMerge = async () => {
    if (!confirm) return;
    const { survivor, losers, plan } = confirm;
    setMerging(true);
    setMergeErr('');
    try {
      // Survivor first: if this fails nothing has been deleted yet.
      await store.saveFactSheet(plan.record);
    } catch (err) {
      setMerging(false);
      setMergeErr(`Could not save "${survivor.name}" — no records were deleted. ${(err && err.message) || ''}`);
      return;
    }
    let deleted = 0;
    const failedDeletes = [];
    for (const l of losers) {
      try {
        await store.deleteFactSheet(l.id);
        deleted += 1;
      } catch (err) {
        failedDeletes.push(l.name);
      }
    }
    setMerging(false);
    setConfirm(null);
    setMergeResult({ survivor: survivor.name, deleted, failedDeletes });
    if (onChanged) onChanged();
    reload();
  };

  const busy = applying || merging;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 700, color: NAVY, pb: 0 }}>
        Bulk tools
        <Typography variant="body2" sx={{ color: GREY, fontWeight: 400 }}>
          Applications in <b>{tenantId}</b> — {loading ? 'loading…' : `${apps.length} records`}
        </Typography>
      </DialogTitle>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 3, borderBottom: '1px solid #e5e9ef' }}>
        <Tab label="Bulk edit" sx={{ fontWeight: 700 }} />
        <Tab label="Find duplicates" sx={{ fontWeight: 700 }} />
      </Tabs>

      <DialogContent dividers sx={{ minHeight: 420 }}>
        {loadErr && <Alert severity="error" sx={{ mb: 2 }}>{loadErr}</Alert>}
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: GREY, mb: 2 }}>
            <CircularProgress size={16} /> <span>Loading applications…</span>
          </Stack>
        )}

        {/* ------------------------------- TAB 1 ------------------------------ */}
        {tab === 0 && (
          <>
            <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1.5 }}>
              <TextField
                size="small" placeholder="Search by name…" value={search}
                onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <TextField select size="small" label="Tag" value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)} sx={{ minWidth: 200 }}>
                <MenuItem value=""><em>Any tag</em></MenuItem>
                {tagOptions.map((t) => (
                  <MenuItem key={t.key} value={t.key}>{t.key} ({t.count})</MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="Lifecycle" value={lifeFilter}
                onChange={(e) => setLifeFilter(e.target.value)} sx={{ minWidth: 160 }}>
                <MenuItem value=""><em>Any lifecycle</em></MenuItem>
                {PICKLISTS.lifecycle.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Disposition (6R)" value={dispFilter}
                onChange={(e) => setDispFilter(e.target.value)} sx={{ minWidth: 170 }}>
                <MenuItem value=""><em>Any disposition</em></MenuItem>
                {PICKLISTS.sixR.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={allMatchingSelected}
                    indeterminate={!allMatchingSelected && someMatchingSelected}
                    onChange={toggleAllMatching}
                    disabled={!filtered.length}
                  />
                }
                label={<span style={{ fontSize: 13, fontWeight: 600 }}>Select all {filtered.length} matching</span>}
              />
              <Chip size="small" label={`${selectedCount} selected`} sx={{ bgcolor: selectedCount ? '#e3f0fa' : undefined, fontWeight: 600 }} />
              {filtered.length > ROW_CAP && (
                <Typography variant="caption" sx={{ color: GREY }}>
                  showing the first {ROW_CAP} rows — "select all matching" still covers all {filtered.length}
                </Typography>
              )}
            </Stack>

            <Paper variant="outlined" sx={{ maxHeight: 260, overflow: 'auto', mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={headSx}>
                    <TableCell padding="checkbox" />
                    <TableCell>Application</TableCell>
                    <TableCell>Lifecycle</TableCell>
                    <TableCell>Hosting</TableCell>
                    <TableCell>6R</TableCell>
                    <TableCell>Complete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((fs) => (
                    <TableRow key={fs.id} hover selected={selected.has(fs.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(fs.id)} onChange={() => toggleRow(fs.id)} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{fs.name}</TableCell>
                      <TableCell sx={{ color: GREY }}>{(fs.fields || {}).lifecycleState || '—'}</TableCell>
                      <TableCell sx={{ color: GREY }}>{(fs.fields || {}).hostingType || '—'}</TableCell>
                      <TableCell sx={{ color: GREY }}>{(fs.fields || {}).sixRClassification || (fs.fields || {}).cmDisposition || '—'}</TableCell>
                      <TableCell><CompletionBar value={fs.completion || 0} width={100} /></TableCell>
                    </TableRow>
                  ))}
                  {!visibleRows.length && !loading && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ color: GREY, textAlign: 'center', py: 3 }}>
                        No applications match these filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1 }}>
              Fields to set
            </Typography>
            <Stack spacing={1}>
              {edits.map((e, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <TextField
                    select size="small" label="Field" value={e.key} sx={{ minWidth: 300 }}
                    onChange={(ev) => {
                      const f = fieldByKey[ev.target.value];
                      setEditAt(i, { key: ev.target.value, value: f && f.type === FIELD_TYPES.BOOLEAN ? false : '' });
                    }}
                  >
                    <MenuItem value=""><em>none</em></MenuItem>
                    {fields.map((f) => (
                      <MenuItem key={f.key} value={f.key}>
                        {f.label}
                        <span style={{ color: '#9AA6B2', marginLeft: 6, fontSize: 12 }}>({f.section})</span>
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <ValueEditor field={fieldByKey[e.key]} value={e.value} onChange={(v) => setEditAt(i, { value: v })} />
                  </Box>
                  <IconButton size="small" disabled={edits.length === 1}
                    onClick={() => { setEdits(edits.filter((_, idx) => idx !== i)); setResult(null); }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1, color: BLUE }}
              onClick={() => setEdits([...edits, { key: '', value: '' }])}>
              Add another field
            </Button>

            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: '#f6f9fc', border: '1px solid #e5e9ef' }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>Preview</Typography>
              {!selectedCount || !validEdits.length ? (
                <Typography variant="body2" sx={{ color: GREY }}>
                  Select at least one application and one field with a value.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2">
                    <b>{selectedCount}</b> application{selectedCount === 1 ? '' : 's'} will be updated:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {validEdits.map((e) => (
                      <li key={e.key}>
                        <Typography variant="body2">
                          {labelFor(e.key)} <b>&rarr;</b>{' '}
                          <b>{typeof e.value === 'boolean' ? (e.value ? 'Yes' : 'No') : String(e.value)}</b>
                        </Typography>
                      </li>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: GREY }}>
                    Other fields on these records are left untouched.
                  </Typography>
                </>
              )}
            </Box>

            {applying && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
            {result && (
              <Alert severity={result.failed ? 'warning' : 'success'} sx={{ mt: 2 }}>
                {result.ok} of {result.count} updated{result.failed ? `, ${result.failed} failed` : ''}.
                {result.errors && result.errors.length ? ` First errors: ${result.errors.join('; ')}` : ''}
              </Alert>
            )}
          </>
        )}

        {/* ------------------------------- TAB 2 ------------------------------ */}
        {tab === 1 && (
          <>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Box sx={{ width: 300 }}>
                <Typography variant="caption" sx={{ color: HEAD, fontWeight: 700 }}>
                  Match threshold: {Math.round(sliderValue * 100)}%
                </Typography>
                <Slider
                  value={sliderValue} min={0.5} max={1} step={0.01}
                  onChange={(e, v) => setSliderValue(Array.isArray(v) ? v[0] : v)}
                  onChangeCommitted={(e, v) => setThreshold(Array.isArray(v) ? v[0] : v)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
                  sx={{ color: BLUE }}
                />
              </Box>
              <Chip label={`${dupes.groups.length} candidate group${dupes.groups.length === 1 ? '' : 's'}`}
                sx={{ fontWeight: 600 }} />
              <Typography variant="caption" sx={{ color: GREY, maxWidth: 420 }}>
                Names are normalised (case, punctuation, "Inc"/"LLC") then scored on edit distance and
                token overlap. Lower the threshold to see looser matches.
              </Typography>
            </Stack>

            {dupes.truncated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This inventory is large — comparison was capped, so a few distant matches may be missing.
                Narrow the portfolio or raise the threshold for a complete pass.
              </Alert>
            )}
            {mergeResult && (
              <Alert severity={mergeResult.failedDeletes.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
                Merged into <b>{mergeResult.survivor}</b>: {mergeResult.deleted} record
                {mergeResult.deleted === 1 ? '' : 's'} deleted.
                {mergeResult.failedDeletes.length
                  ? ` Could not delete: ${mergeResult.failedDeletes.join(', ')}.`
                  : ''}
              </Alert>
            )}
            {mergeErr && <Alert severity="error" sx={{ mb: 2 }}>{mergeErr}</Alert>}

            {!dupes.groups.length && !loading && (
              <Typography variant="body2" sx={{ color: GREY, py: 4, textAlign: 'center' }}>
                No duplicate candidates at {Math.round(threshold * 100)}%.
              </Typography>
            )}

            {dupes.groups.slice(0, GROUP_CAP).map((g) => {
              const sid = survivorFor(g);
              const losers = losersFor(g);
              return (
                <Paper key={g.id} variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, color: HEAD }}>
                      {g.members.map((m) => m.name).join('  ·  ')}
                    </Typography>
                    <Chip size="small" label={`${Math.round(g.score * 100)}% similar`} sx={{ bgcolor: '#e3f0fa', fontWeight: 600 }} />
                    <Chip size="small" variant="outlined" label={g.members[0].tenantId} />
                  </Stack>

                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={headSx}>
                          <TableCell sx={{ minWidth: 150 }}>Field</TableCell>
                          {g.members.map((m) => (
                            <TableCell key={m.id} sx={{ minWidth: 220, verticalAlign: 'top' }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Radio size="small" checked={m.id === sid}
                                  onChange={() => setSurvivors({ ...survivors, [g.id]: m.id })} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.name}</Typography>
                                  <Typography variant="caption" sx={{ color: GREY }}>
                                    {m.id === sid ? 'survivor (keeps its values)' : 'will be merged away'}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Box sx={{ mt: 0.5 }}><CompletionBar value={m.completion || 0} width={130} /></Box>
                              <Typography variant="caption" sx={{ color: GREY }}>
                                {(m.tags || []).length} tags · {(m.integrations || []).length} integrations
                              </Typography>
                              {m.id !== sid && (
                                <FormControlLabel
                                  sx={{ m: 0, display: 'block' }}
                                  control={
                                    <Checkbox size="small"
                                      checked={!dropped.has(`${g.id}::${m.id}`)}
                                      onChange={() => toggleDropped(g.id, m.id)} />
                                  }
                                  label={<span style={{ fontSize: 12 }}>include in merge</span>}
                                />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {COMPARE_FIELDS.map((key) => (
                          <TableRow key={key}>
                            <TableCell sx={{ fontWeight: 600, color: GREY }}>{labelFor(key)}</TableCell>
                            {g.members.map((m) => {
                              const v = (m.fields || {})[key];
                              return (
                                <TableCell key={m.id} sx={{
                                  color: isBlank(v) ? '#b0bac4' : 'inherit',
                                  maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  {isBlank(v) ? '—' : String(v)}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button
                      size="small" variant="outlined" startIcon={<CallMergeIcon />} disabled={!losers.length || busy}
                      onClick={() => openConfirm(g)}
                      sx={{ borderColor: BLUE, color: BLUE }}
                    >
                      Merge {losers.length} into {g.members.find((m) => m.id === sid).name}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}

            {dupes.groups.length > GROUP_CAP && (
              <Typography variant="caption" sx={{ color: GREY }}>
                Showing the {GROUP_CAP} strongest of {dupes.groups.length} groups.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        {tab === 0 && (
          <Button
            variant="contained" onClick={applyEdits}
            disabled={busy || !selectedCount || !validEdits.length}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {applying ? `Applying ${progress}%` : `Apply to ${selectedCount} application${selectedCount === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>

      {/* ----------------------- Destructive merge confirm ---------------------- */}
      <Dialog open={!!confirm} onClose={merging ? undefined : () => setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#F5A623' }} /> Confirm merge — this deletes records
        </DialogTitle>
        <DialogContent dividers>
          {confirm && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>{confirm.survivor.name}</b> will be kept. It keeps every value it already has.
              </Typography>

              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mt: 1.5 }}>
                Copied into the survivor ({confirm.plan.filled.length} empty field
                {confirm.plan.filled.length === 1 ? '' : 's'} back-filled)
              </Typography>
              {confirm.plan.filled.length ? (
                <Box component="ul" sx={{ m: 0, pl: 3, maxHeight: 180, overflow: 'auto' }}>
                  {confirm.plan.filled.map((f, i) => (
                    <li key={`${f.key}-${i}`}>
                      <Typography variant="body2">
                        {labelFor(f.key)} <b>&rarr;</b> {String(f.value).slice(0, 90)}
                        <span style={{ color: GREY }}> (from {f.from})</span>
                      </Typography>
                    </li>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: GREY }}>
                  Nothing to back-fill — the survivor already has a value everywhere.
                </Typography>
              )}

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Tags added: <b>{confirm.plan.tagsAdded.length}</b>
                {confirm.plan.tagsAdded.length
                  ? ` (${confirm.plan.tagsAdded.map((t) => `${t.group}: ${t.value}`).join(', ')})`
                  : ''}
                {' · '}Integrations added: <b>{confirm.plan.integrationsAdded.length}</b>
                {' · '}Relations added: <b>{confirm.plan.relationsAdded}</b>
              </Typography>

              <Alert severity="warning" sx={{ mt: 2 }}>
                These {confirm.losers.length} record{confirm.losers.length === 1 ? '' : 's'} will be
                permanently deleted after the survivor is saved:
                <b> {confirm.losers.map((l) => l.name).join(', ')}</b>. This cannot be undone.
              </Alert>
              {merging && <LinearProgress sx={{ mt: 2 }} />}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={merging}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doMerge} disabled={merging}>
            {merging ? 'Merging…' : 'Merge and delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
